import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '@services/auth.service';

export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.user()) {
    return of(true);
  }

  // Cold visit: this browser never held a session (or it ended cleanly), so
  // /auth/me can only answer 401 — and the interceptor would then fire a second
  // failing /auth/refresh and a "session expired" toast. Go straight to login.
  if (!auth.hasSessionHint()) {
    return of(router.parseUrl('/login'));
  }

  return auth.me().pipe(
    map(() => true),
    catchError((err: unknown) => {
      // Only a refused session sends the user to /login. A rate limit, a 5xx
      // or a dropped connection says nothing about the session, which may be
      // alive; the interceptor has already told the user what went wrong.
      const status = (err as { error?: { statusCode?: number } } | null)?.error?.statusCode;
      if (status === 401 || status === 403) {
        void router.navigate(['/login']);
      }
      return of(false);
    }),
  );
};
